import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, NgModule} from '@angular/core';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {UiSwitchModule} from 'ngx-ui-switch';
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {AccordionModule} from 'ngx-bootstrap/accordion';
import {ModalModule} from 'ngx-bootstrap/modal';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HeaderComponent} from './core/header/header.component';
import {SessionsComponent} from './core/sessions/sessions.component';
import {AuthenticatorsComponent} from './core/authenticators/authenticators.component';
import {EnrollmentComponent} from './core/enrollment/enrollment.component';
import {ToastrModule} from 'ngx-toastr';
import {ProgressbarModule} from 'ngx-bootstrap/progressbar';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {InterceptorService} from './shared/services/interceptor.service';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {IdentityComponent} from './core/identity/identity.component';
import {ConfirmationModalComponent} from './shared/components/confirmation-modal/confirmation-modal.component';

import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {environment} from 'src/environments/environment';
import {NgApexchartsModule} from 'ng-apexcharts';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
  faAsterisk,
  faCamera,
  faChevronDown,
  faChevronUp,
  faClock,
  faComputer,
  faEllipsisV,
  faEye,
  faFingerprint,
  faKey,
  faLaptopCode,
  faMobile,
  faMobileAlt,
  faQuestion,
  faSms,
  faUserLock
} from '@fortawesome/free-solid-svg-icons';
import {faUsb} from '@fortawesome/free-brands-svg-icons';
import {faHandPaper} from '@fortawesome/free-regular-svg-icons';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {HomeComponent} from './core/home/home.component';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {ErrorComponent} from './core/error/error.component';
import {ApplicationsComponent} from "./core/applications/applications.component";
import {CardComponent} from "./shared/components/card/card.component";
import {QRCodeModule} from "angularx-qrcode";
import {TranslationLoaderService} from "./shared/services/translation-loader";
import {OtherAuthenticatorsComponent} from "./core/authenticators/other-authenticators/other-authenticators.component";
import {CommonModule} from "./common.module";
import {
  DeviceAuthenticatorsComponent
} from "./core/authenticators/device-authenticators/device-authenticators.component";

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, environment.API_URL + "rest/resources/translation/", "");
}

export function initializeApp(translationLoader: TranslationLoaderService): () => Promise<void> {
  return () => translationLoader.loadTranslations();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SessionsComponent,
    AuthenticatorsComponent,
    EnrollmentComponent,
    IdentityComponent,
    ConfirmationModalComponent,
    CardComponent,
    HomeComponent,
    ErrorComponent,
    ApplicationsComponent
  ],
  bootstrap: [AppComponent],
  exports: [],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CommonModule,
    NgxDatatableModule,
    UiSwitchModule,
    FormsModule,
    BsDatepickerModule.forRoot(),
    ReactiveFormsModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: false,
      progressBar: true,
      progressAnimation: 'increasing',
      closeButton: true
    }),
    ProgressbarModule.forRoot(),
    TooltipModule.forRoot(),
    BsDropdownModule.forRoot(),
    AccordionModule.forRoot(),
    ModalModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgApexchartsModule,
    FontAwesomeModule,
    QRCodeModule,
    CollapseModule,
    OtherAuthenticatorsComponent,
    DeviceAuthenticatorsComponent
  ],
  providers: [
    TranslationLoaderService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [TranslationLoaderService],
      multi: true,
    },
    {provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true},
    {provide: LocationStrategy, useClass: HashLocationStrategy},
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {

  constructor(library: FaIconLibrary) {
    // Add fa icons to the library for convenient access in other components
    library.addIcons(faMobileAlt);
    library.addIcons(faComputer);
    library.addIcons(faKey);
    library.addIcons(faSms);
    library.addIcons(faClock);
    library.addIcons(faAsterisk);
    library.addIcons(faUsb);
    library.addIcons(faFingerprint);
    library.addIcons(faCamera);
    library.addIcons(faEllipsisV);
    library.addIcons(faEye);
    library.addIcons(faHandPaper);
    library.addIcons(faQuestion);
    library.addIcons(faChevronUp);
    library.addIcons(faChevronDown);
    library.addIcons(faLaptopCode);
    library.addIcons(faMobile);
    library.addIcons(faUserLock);
  }
}
